import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import ResponsiveLayout from '../components/ResponsiveLayout';
import { ArrowLeft, Award, BarChart3, Brain, Building, Calendar, CheckCircle, ChevronRight, ClipboardList, Clock, CreditCard, Download, Eye, FileText, Home, IndianRupee, Mail, Minus, Phone, Plus, Printer, School, Shield, TrendingUp, User, UserCheck, Users } from 'lucide-react';

import { principalNavItems } from '../config/principalNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PrincipalPayslips = () => {
  const navigate = useNavigate();
  const { year, month } = useParams();
  
  const [payslips, setPayslips] = useState([]);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(year ? parseInt(year) : new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(month ? parseInt(month) : new Date().getMonth() + 1);

  const getToken = () => localStorage.getItem('principal_token');

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/principal/login');
      return;
    }
    loadPayslips();
  }, [navigate]);

  useEffect(() => {
    if (year && month) {
      loadSpecificPayslip(parseInt(year), parseInt(month));
    }
  }, [year, month]);

  const loadPayslips = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${BACKEND_URL}/api/principal/payslips?token=${token}`);
      setPayslips(response.data.payslips || []);
    } catch (error) {
      console.error('Error loading payslips:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSpecificPayslip = async (yr, mo) => {
    try {
      const token = getToken();
      const response = await axios.get(`${BACKEND_URL}/api/principal/payslip/${yr}/${mo}?token=${token}`);
      setSelectedPayslip(response.data);
      setSelectedYear(yr);
      setSelectedMonth(mo);
    } catch (error) {
      console.error('Error loading payslip:', error);
    }
  };

  const generatePayslip = async () => {
    try {
      setLoading(true);
      await loadSpecificPayslip(selectedYear, selectedMonth);
      loadPayslips();
    } catch (error) {
      console.error('Error generating payslip:', error);
      alert('Failed to generate payslip');
    } finally {
      setLoading(false);
    }
  };

  const printPayslip = () => {
    window.print();
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sanjaya-navy"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100" data-testid="principal-payslips-page">
      
      
      <div className="pt-4 pb-8 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <FileText className="w-7 h-7 text-sanjaya-navy" />
                Pay Slips
              </h1>
              <p className="text-slate-600">View and download your monthly pay slips</p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/principal/business')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Business
            </Button>
          </div>

          {/* Month Selector */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div>
                  <label className="text-sm text-slate-600 block mb-1">Year</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="px-3 py-2 border rounded-lg"
                  >
                    {[2024, 2025, 2026].map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-600 block mb-1">Month</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="px-3 py-2 border rounded-lg"
                  >
                    {months.map((m, idx) => (
                      <option key={idx} value={idx + 1}>{m}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1" />
                <Button onClick={generatePayslip} className="bg-sanjaya-navy hover:bg-sanjaya-navy-dark">
                  <FileText className="w-4 h-4 mr-2" />
                  Generate Pay Slip
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Pay Slip History */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Pay Slip History</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {payslips.length === 0 ? (
                    <div className="p-6 text-center text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      <p>No pay slips yet</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {payslips.map((ps, idx) => (
                        <div
                          key={ps.id || idx}
                          className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                            selectedPayslip?.id === ps.id ? 'bg-blue-50 border-l-4 border-sanjaya-navy' : ''
                          }`}
                          onClick={() => setSelectedPayslip(ps)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-slate-800">{ps.month_name}</p>
                              <p className="text-sm text-slate-500">{ps.payslip_number}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-green-600">₹{ps.net_earnings?.toLocaleString()}</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                ps.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {ps.status === 'paid' ? 'Paid' : 'Pending'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Pay Slip Detail */}
            <div className="lg:col-span-2">
              {selectedPayslip ? (
                <Card className="print:shadow-none" id="payslip-content">
                  {/* Company Header */}
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 print:bg-sanjaya-navy">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-bold">{selectedPayslip.company?.name}</h2>
                        <p className="text-blue-200 text-sm">{selectedPayslip.company?.address}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold">PAY SLIP</p>
                        <p className="text-blue-200">{selectedPayslip.month_name}</p>
                      </div>
                    </div>
                  </div>

                  <CardContent className="p-6">
                    {/* Employee Details */}
                    <div className="grid md:grid-cols-2 gap-6 mb-6 pb-6 border-b">
                      <div>
                        <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <User className="w-4 h-4" />
                          Principal Details
                        </h3>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-slate-500">Name:</span> <span className="font-medium">{selectedPayslip.principal_name}</span></p>
                          <p><span className="text-slate-500">Email:</span> {selectedPayslip.principal_email}</p>
                          <p><span className="text-slate-500">Designation:</span> {selectedPayslip.designation}</p>
                          <p><span className="text-slate-500">School:</span> {selectedPayslip.school}</p>
                          <p><span className="text-slate-500">PAN:</span> {selectedPayslip.pan}</p>
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Pay Slip Details
                        </h3>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-slate-500">Pay Slip No:</span> <span className="font-medium">{selectedPayslip.payslip_number}</span></p>
                          <p><span className="text-slate-500">Pay Period:</span> {selectedPayslip.month_name}</p>
                          <p><span className="text-slate-500">Bank A/C:</span> {selectedPayslip.bank_account}</p>
                          <p><span className="text-slate-500">Status:</span> 
                            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                              selectedPayslip.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {selectedPayslip.status === 'paid' ? 'Paid' : 'Pending'}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Earnings & Deductions */}
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      {/* Earnings */}
                      <div>
                        <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                          <Plus className="w-4 h-4" />
                          Earnings
                        </h3>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Student Fee ({selectedPayslip.earnings?.active_students} × ₹{selectedPayslip.earnings?.per_student_rate})</span>
                            <span className="font-medium">₹{selectedPayslip.earnings?.student_earnings?.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Program Management Fee</span>
                            <span className="font-medium">₹{selectedPayslip.earnings?.management_fee?.toLocaleString()}</span>
                          </div>
                          {selectedPayslip.earnings?.enrollment_bonus > 0 && (
                            <div className="flex justify-between text-sm">
                              <span>Enrollment Bonus ({selectedPayslip.new_enrollments} new)</span>
                              <span className="font-medium">₹{selectedPayslip.earnings?.enrollment_bonus?.toLocaleString()}</span>
                            </div>
                          )}
                          {selectedPayslip.earnings?.retention_bonus > 0 && (
                            <div className="flex justify-between text-sm">
                              <span>Retention Bonus</span>
                              <span className="font-medium">₹{selectedPayslip.earnings?.retention_bonus?.toLocaleString()}</span>
                            </div>
                          )}
                          <div className="flex justify-between pt-2 border-t font-semibold text-green-700">
                            <span>Gross Earnings</span>
                            <span>₹{selectedPayslip.earnings?.gross_earnings?.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      {/* Deductions */}
                      <div>
                        <h3 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                          <Minus className="w-4 h-4" />
                          Deductions
                        </h3>
                        <div className="space-y-2">
                          {selectedPayslip.deductions?.tds > 0 && (
                            <div className="flex justify-between text-sm">
                              <span>TDS ({selectedPayslip.deductions?.tds_rate}%)</span>
                              <span className="font-medium">₹{selectedPayslip.deductions?.tds?.toLocaleString()}</span>
                            </div>
                          )}
                          {selectedPayslip.deductions?.professional_tax > 0 && (
                            <div className="flex justify-between text-sm">
                              <span>Professional Tax</span>
                              <span className="font-medium">₹{selectedPayslip.deductions?.professional_tax?.toLocaleString()}</span>
                            </div>
                          )}
                          {selectedPayslip.deductions?.total_deductions === 0 && (
                            <p className="text-sm text-slate-500">No deductions</p>
                          )}
                          <div className="flex justify-between pt-2 border-t font-semibold text-red-700">
                            <span>Total Deductions</span>
                            <span>₹{selectedPayslip.deductions?.total_deductions?.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Statistics */}
                    <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                      <div className="text-center">
                        <Users className="w-6 h-6 mx-auto mb-1 text-blue-500" />
                        <p className="text-2xl font-bold text-slate-800">{selectedPayslip.earnings?.active_students}</p>
                        <p className="text-xs text-slate-500">Active Students</p>
                      </div>
                      <div className="text-center">
                        <TrendingUp className="w-6 h-6 mx-auto mb-1 text-green-500" />
                        <p className="text-2xl font-bold text-slate-800">{selectedPayslip.new_enrollments || 0}</p>
                        <p className="text-xs text-slate-500">New This Month</p>
                      </div>
                      <div className="text-center">
                        <Award className="w-6 h-6 mx-auto mb-1 text-yellow-500" />
                        <p className="text-2xl font-bold text-slate-800">₹{selectedPayslip.earnings?.total_bonuses?.toLocaleString()}</p>
                        <p className="text-xs text-slate-500">Bonuses</p>
                      </div>
                    </div>

                    {/* Net Pay */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-slate-600">Net Pay</p>
                          <p className="text-3xl font-bold text-green-700">₹{selectedPayslip.net_earnings?.toLocaleString()}</p>
                        </div>
                        <div className="flex gap-2 print:hidden">
                          <Button variant="outline" onClick={printPayslip}>
                            <Printer className="w-4 h-4 mr-2" />
                            Print
                          </Button>
                          <Button className="bg-green-600 hover:bg-green-700">
                            <Download className="w-4 h-4 mr-2" />
                            Download PDF
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="mt-6 pt-4 border-t text-center text-xs text-slate-400">
                      <p>This is a computer-generated document. No signature required.</p>
                      <p className="mt-1">
                        Generated on {new Date(selectedPayslip.generated_at).toLocaleString()} | 
                        {selectedPayslip.company?.pan} | {selectedPayslip.company?.tan}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">Select a Pay Slip</h3>
                    <p className="text-slate-500">Choose a pay slip from the history or generate a new one</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #payslip-content, #payslip-content * { visibility: visible; }
          #payslip-content { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default PrincipalPayslips;
